import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Row, Col } from "reactstrap";
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import { useDarkMode } from "../../../context/DarkModeProvider";
import { useLanguage } from "context/LanguageProvider";

const InsertDeleteChaining = () => {

    const [stage, setStage] = useState(1); // 1 = Hash Defender, 2 = Memory Chaos
    const [level, setLevel] = useState(1);
    const [lives, setLives] = useState(3);

    const [isPlaying, setIsPlaying] = useState(false);
    const { darkMode } = useDarkMode();

    // Hash Table
    const [buckets, setBuckets] = useState([]);
    const numBuckets = 8;
    const maxChainLength = 5; // For stage 1
    const maxBucketCapacity = 3; // For stage 2

    // Incoming elements
    const [incomingElements, setIncomingElements] = useState([]);

    // Timing
    const [timeRemaining, setTimeRemaining] = useState(100);
    const maxTime = 100;
    const [spawnRate, setSpawnRate] = useState(3000);

    // Progress
    const [elementsPlaced, setElementsPlaced] = useState(0);
    const [elementsNeeded, setElementsNeeded] = useState(8); // Αλλαγή από 15 σε 8
    const [roundsPassed, setRoundsPassed] = useState(0);
    const roundsNeeded = 3;

    // Modals
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const [gameOverMessage, setGameOverMessage] = useState("");
    const [gameWonMessage, setGameWonMessage] = useState("");
    const [hashMod, setHashMod] = useState(null);
    const [wrongDropIndex, setWrongDropIndex] = useState(null);

    // Status message
    const [statusMessage, setStatusMessage] = useState("");
    const [statusType, setStatusType] = useState("");
    const [wrongNodes, setWrongNodes] = useState([]);
    const [wrongBuckets, setWrongBuckets] = useState([]);


    const HashFunctVar = [3, 4, 6, 7, 8, 9]
    const spawnIntervalRef = useRef(null);
    const timerIntervalRef = useRef(null);

    const { selLanguage, toggleLanguage, isLoading } = useLanguage();
    const mission = selLanguage ? "Πρέπει να τοποθετείς τα στοιχεία που θα εμφανίζονται στα σωστά buckets σύμφωνα με τη hash function που σου δίνεται όταν πατήσεις το κουμπί 'Έναρξη' στο τέλος της σελίδας. Για να σύρεις τους κόμβους όταν τους κρατάς, μετακίνησέ τους κοντά στο τέλος ή στην κορυφή της οθόνης." : "You must place the elements that appear into the correct buckets according to the given hash function when you press the 'Start' button at the bottom of the page. To drag the nodes when you grab them move them close to the end or the top of the screen."

    const hashFunction = (value) => {
        if (!hashMod) return 0;
        return value % hashMod;
    };


    // Initialize buckets
    useEffect(() => {
        const initialBuckets = [];
        for (let i = 0; i < numBuckets; i++) {
            initialBuckets.push([]);
        }
        setBuckets(initialBuckets);
    }, []);


    // Auto-scroll during drag
    useEffect(() => {
        const handleDragScroll = (e) => {
            const container = document.querySelector('.hash-table-container');
            if (!container) return;

            const rect = container.getBoundingClientRect();
            const scrollSpeed = 10;

            // Scroll up if near top
            if (e.clientY < rect.top + 50) {
                container.scrollTop -= scrollSpeed;
            }
            // Scroll down if near bottom
            else if (e.clientY > rect.bottom - 50) {
                container.scrollTop += scrollSpeed;
            }
        };

        if (isPlaying) {
            document.addEventListener('drag', handleDragScroll);
        }

        return () => {
            document.removeEventListener('drag', handleDragScroll);
        };
    }, [isPlaying]);


    // Check for game over when lives change
    useEffect(() => {
        if (lives <= 0 && isPlaying) {
            handleGameOver("💔 Έμειναν οι ζωές σου!");
        }
    }, [lives]);

    // Timer effect
    useEffect(() => {
        if (isPlaying) {
            timerIntervalRef.current = setInterval(() => {
                setTimeRemaining(prev => {
                    if (prev <= 0.5) {
                        handleGameOver("⏰ Τέλειωσε ο χρόνος!");
                        return 0;
                    }
                    return prev - 0.5;
                });
            }, 500);
        }

        return () => {
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
            }
        };
    }, [isPlaying]);

    // Spawn elements effect
    useEffect(() => {
        if (isPlaying) {
            spawnIntervalRef.current = setInterval(() => {
                spawnElement();
            }, spawnRate);
        }

        return () => {
            if (spawnIntervalRef.current) {
                clearInterval(spawnIntervalRef.current);
            }
        };
    }, [isPlaying, spawnRate]);

    // Spawn incoming element
    const spawnElement = () => {
        setIncomingElements(prev => {
            const newElements = [...prev];
            const value = Math.floor(Math.random() * 100);
            newElements.push(value);

            if (newElements.length > 5) {
                handleGameOver("Πάρα πολλά στοιχεία σε αναμονή! ⏱️");
                return prev;
            }

            return newElements;
        });
    };

    const handleDrop = (e, bucketIndex) => {
        if (!isPlaying) return;

        const value = parseInt(e.dataTransfer.getData("value"));
        const queueIndex = parseInt(e.dataTransfer.getData("queueIndex"));
        const correctBucket = hashFunction(value);

        if (bucketIndex === correctBucket) {
            // ✅ Σωστό bucket
            setBuckets(prev => {
                const newBuckets = [...prev];
                newBuckets[bucketIndex] = [...newBuckets[bucketIndex], value];
                return newBuckets;
            });

            setIncomingElements(prev => prev.filter((_, idx) => idx !== queueIndex));

            setElementsPlaced(e => {
                const newPlaced = e + 1;
                if (newPlaced >= elementsNeeded) {
                    completeRound();
                }
                return newPlaced;
            });
        } else {
            // ❌ Λάθος bucket
            setLives(l => l - 1);
            showStatus(" Λάθος bucket! -1 ζωή", "error");

            // Μαρκάρουμε το λάθος bucket
            setWrongBuckets(prev => [...prev, bucketIndex]);

            // Αφαιρούμε τον κόμβο από την ουρά
            setIncomingElements(prev => prev.filter((_, idx) => idx !== queueIndex));

            // Μετά από 600ms, το bucket επανέρχεται στο κανονικό
            setTimeout(() => {
                setWrongBuckets(prev => prev.filter(idx => idx !== bucketIndex));
            }, 600);
        }


    };



    const removeFromBucket = (bucketIndex, itemIndex) => {
        if (!isPlaying || stage !== 2) return;

        setBuckets(prev => {
            const newBuckets = [...prev];
            newBuckets[bucketIndex] = newBuckets[bucketIndex].filter((_, idx) => idx !== itemIndex);
            return newBuckets;
        });


        showStatus("Στοιχείο διαγράφηκε!", "success");
    };

    // Complete round
    const completeRound = () => {
        setIsPlaying(false);
        const message = `🎉 Κέρδισες! Τοποθέτησες ${elementsNeeded} στοιχεία σωστά!`;
        setGameWonMessage(message);
        setShowGameWon(true);
    };

    // Win level
    const handleWinLevel = () => {
        setIsPlaying(false);
        const message = stage === 1
            ? `Κέρδισες το Hash Defender! `
            : `Κέρδισες το Memory Chaos!`;
        setGameWonMessage(message);
        setShowGameWon(true);
    };

    // Game over
    const handleGameOver = (reason) => {
        setIsPlaying(false);
        setGameOverMessage(reason);
        setShowGameOver(true);
    };

    // Start game
    const startGame = () => {
        if (isPlaying) return;

        const randomMod = HashFunctVar[Math.floor(Math.random() * HashFunctVar.length)];
        setHashMod(randomMod);

        setIsPlaying(true);
        setElementsPlaced(0);
        setRoundsPassed(0);
        setIncomingElements([]);
        setTimeRemaining(maxTime);

        // Clear buckets
        const emptyBuckets = [];
        for (let i = 0; i < numBuckets; i++) {
            emptyBuckets.push([]);
        }
        setBuckets(emptyBuckets);

        // Set difficulty
        const baseSpawnRate = stage === 1 ? 3000 : 2500;
        setSpawnRate(Math.max(1000, baseSpawnRate - (level - 1) * 400));
        setElementsNeeded(8); // Πάντα 8 στοιχεία για νίκη

        showStatus(selLanguage ? " Παιχνίδι ξεκίνησε!" : "Game started!", "success");
    };

    // Reset game - Τώρα αλλάζει και το hash function
    const resetGame = () => {
        setIsPlaying(false);
        setLives(3);

        setElementsPlaced(0);
        setRoundsPassed(0);
        setIncomingElements([]);
        setTimeRemaining(maxTime);
        setShowGameOver(false);
        setShowGameWon(false);

        // Νέο hash function
        const randomMod = HashFunctVar[Math.floor(Math.random() * HashFunctVar.length)];
        setHashMod(randomMod);

        const emptyBuckets = [];
        for (let i = 0; i < numBuckets; i++) {
            emptyBuckets.push([]);
        }
        setBuckets(emptyBuckets);

        showStatus("", "");
    };

    // Next level
    const nextLevel = () => {
        setShowGameWon(false);

        if (stage === 1) {
            setStage(2);
            setLevel(1);

            setLives(3);
            showStatus(selLanguage ? " Καλώς ήρθες στο Memory Chaos!" : "Welcome to Memory Chaos!", selLanguage ? "Προειδοποίηση" : "warning");
        } else {
            setLevel(l => l + 1);

            setLives(3);
            showStatus(selLanguage ? `Επίπεδο ${level + 1} - Δυσκολία αυξάνεται!` : `Level ${level + 1} - Difficulty increases!`, selLanguage ? "Προειδοποίηση" : "warning");
        }

        // Νέο hash function
        const randomMod = HashFunctVar[Math.floor(Math.random() * HashFunctVar.length)];
        setHashMod(randomMod);

        const emptyBuckets = [];
        for (let i = 0; i < numBuckets; i++) {
            emptyBuckets.push([]);
        }
        setBuckets(emptyBuckets);
        setIncomingElements([]);
        setTimeRemaining(maxTime);
    };

    // Show status message
    const showStatus = (message, type) => {
        setStatusMessage(message);
        setStatusType(type);

        if (message) {
            setTimeout(() => {
                setStatusMessage("");
                setStatusType("");
            }, 3000);
        }
    };

    const selectedStage = JSON.parse(localStorage.getItem("selectedStage"));
    var games = selectedStage.games;
    var nameGameKey = localStorage.getItem("gameClicked");
    var selectedGame = null;
    for (let i = 0; i < games.length; i++) {
        if (games[i].game_title === nameGameKey) {
            selectedGame = games[i];
            break;
        }
    }


    return (
        <div
            className='main-content'
            style={{
                minHeight: "100vh",
                background: darkMode ? "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #101724ff 100%)" : "var(--lightModeGameBackground)",
                padding: "30px 10px",
                borderRadius: "10px",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                overflowX: "hidden"
            }}>
            <style>{`
                @keyframes heartbeat {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.1); }
                }
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideUp {
                    from {
                        transform: translateY(50px);
                        opacity: 0;
                    }
                    to {
                        transform: translateY(0);
                        opacity: 1;
                    }
                }
                @keyframes slideIn {
                    from {
                        opacity: 0;
                        transform: translateY(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                @keyframes pulse {
                    0%, 100% { 
                        transform: scale(1);
                        box-shadow: 0 4px 15px rgba(239, 68, 68, 0.4);
                    }
                    50% { 
                        transform: scale(1.05);
                        box-shadow: 0 8px 25px rgba(239, 68, 68, 0.8);
                    }
                }
                @keyframes popIn {
                    0% {
                        opacity: 0;
                        transform: scale(0.5);
                    }
                    100% {
                        opacity: 1;
                        transform: scale(1);
                    }
                }
                @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    25% { transform: translateX(-10px); }
                    75% { transform: translateX(10px); }
                }
            `}</style>

            <GameOverModal
                isVisible={showGameOver}
                onPlayAgain={resetGame}
                message={gameOverMessage}
            />

            <GameWonModal
                isVisible={showGameWon}
                onPlayAgain={resetGame}
                onNextLevel={nextLevel}
                message={gameWonMessage}
            />

            <Col xl="12" lg="12" md="12" sm="12" xs="12" style={{ maxWidth: "900px", width: "100%" }}>
                <div className="max-w-7xl mx-auto">
                    {/* Lives Display */}
                    <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", marginBottom: "20px" }}>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            flexDirection: "row",
                            justifyContent: "right",
                            gap: "12px"
                        }}>

                            <div style={{
                                display: "flex",
                                gap: "6px",
                                alignItems: "center"
                            }}>
                                {[1, 2, 3].map((heartNum) => (
                                    <div
                                        key={heartNum}
                                        style={{
                                            fontSize: "24px",
                                            filter: lives >= heartNum
                                                ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                                : "grayscale(100%) opacity(0.3)",
                                            transform: lives >= heartNum ? "scale(1)" : "scale(0.8)",
                                            transition: "all 0.3s ease",
                                            animation: lives >= heartNum ? "heartbeat 2s ease-in-out infinite" : "none"
                                        }}
                                    >
                                        ❤️
                                    </div>
                                ))}
                            </div>

                        </div>
                    </div>

                    <div style={{
                        borderRadius: "20px",
                        maxWidth: "770px",
                        background: "linear-gradient(135deg, rgba(59, 130, 246, 0.4), rgba(29, 78, 216, 0.4))",
                        width: "90%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: "15px 20px",
                        margin: "0 auto 20px",
                        color: "white",
                        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                    }}>
                        {mission}
                    </div>

                    {/* Game Info */}
                    <div style={{
                        display: "flex",
                        justifyContent: "space-around",
                        flexWrap: "wrap",
                        gap: "15px",
                        marginBottom: "20px",
                        background: "rgba(59, 130, 246, 0.15)",
                        padding: "15px",
                        borderRadius: "16px",
                        border: "2px solid rgba(59, 130, 246, 0.3)"
                    }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "5px" }}>
                            <span style={{ color: "#94a3b8", fontSize: "12px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "1px" }}>
                                {selLanguage ? "Πρόοδος" : "Progress"}
                            </span>
                            <span style={{ color: "#ffffff", fontSize: "20px", fontWeight: "700", textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)" }}>
                                {elementsPlaced}/{elementsNeeded}
                            </span>
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "5px" }}>
                            <span style={{ color: "#94a3b8", fontSize: "12px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "1px" }}>
                                Hash Function
                            </span>
                            <span style={{ color: "#ffffff", fontSize: "20px", fontWeight: "700", textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)" }}>
                                {hashMod ? `value % ${hashMod}` : "-"}
                            </span>
                        </div>

                    </div>



                    {/* Timer Bar */}
                    <div style={{
                        width: "100%",
                        height: "12px",
                        background: "rgba(15, 23, 42, 0.6)",
                        borderRadius: "10px",
                        overflow: "hidden",
                        marginBottom: "20px",
                        border: "2px solid rgba(59, 130, 246, 0.3)"
                    }}>
                        <div style={{
                            height: "100%",
                            background: "linear-gradient(90deg, #10b981 0%, #f59e0b 50%, #ef4444 100%)",
                            width: `${(timeRemaining / maxTime) * 100}%`,
                            transition: "width 0.1s linear",
                            boxShadow: "0 0 10px rgba(16, 185, 129, 0.6)"
                        }} />
                    </div>

                    {/* Incoming Queue */}
                    <div style={{
                        display: "flex",
                        gap: "15px",
                        justifyContent: "center",
                        alignItems: "center",
                        marginBottom: "24px",
                        width: "100%",
                        minHeight: "70px",
                        flexWrap: "wrap"
                    }}>
                        {incomingElements.map((value, index) => (
                            <motion.div
                                key={index}
                                draggable
                                onDragStart={(e) => {
                                    e.dataTransfer.setData("value", value);
                                    e.dataTransfer.setData("queueIndex", index);
                                }}
                                initial={{ opacity: 0, y: -20 }}
                                animate={
                                    wrongNodes.includes(index)
                                        ? { backgroundColor: "#ef4444", opacity: 0, scale: 0.5 }
                                        : { opacity: 1, y: 0, scale: 1 }
                                }
                                transition={{ duration: 0.5 }}
                                style={{
                                    width: "60px",
                                    height: "60px",
                                    borderRadius: "12px",
                                    background: wrongNodes.includes(index)
                                        ? "linear-gradient(135deg, #ef4444, #b91c1c)"
                                        : "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                                    color: "#ffffff",
                                    fontWeight: "700",
                                    fontSize: "1.3rem",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: "0 4px 15px rgba(59, 130, 246, 0.4)",
                                    cursor: "pointer",
                                    border: "3px solid rgba(255, 255, 255, 0.3)",
                                    userSelect: "none"
                                }}
                            >
                                {value}
                            </motion.div>
                        ))}

                    </div>

                    {/* Hash Table Frame - Μικρότερο */}
                    <div className="hash-table-container" style={{
                        position: "relative",
                        width: "100%",
                        maxWidth: "700px",
                        minHeight: "320px",
                        border: "3px solid #3b82f6",
                        borderRadius: "16px",
                        margin: "0 auto 30px",
                        background: "#ffffffde",
                        boxShadow: "inset 0 2px 10px rgba(0, 0, 0, 0.1), 0 10px 25px rgba(59, 130, 246, 0.15)",
                        overflowY: "auto",
                        overflowX: "hidden",
                        padding: "15px"
                    }}>
                        <div style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "10px",
                            width: "100%"
                        }}>
                            {buckets.map((bucket, bucketIndex) => {
                                const maxCapacity = stage === 1 ? maxChainLength : maxBucketCapacity;
                                const isOverflow = bucket.length > maxCapacity;

                                return (
                                    <div
                                        key={bucketIndex}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => handleDrop(e, bucketIndex)}
                                        style={{
                                            background: wrongBuckets.includes(bucketIndex)
                                                ? "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)"
                                                : "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                                            border: wrongBuckets.includes(bucketIndex)
                                                ? "2px solid #ef4444"
                                                : "2px solid #94a3b8",
                                            borderRadius: "10px",
                                            padding: "10px 12px",
                                            transition: "all 0.3s ease",
                                            animation: wrongBuckets.includes(bucketIndex) ? "shake 0.5s ease" : "none"
                                        }}
                                    >

                                        <div style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginBottom: "8px",
                                            paddingBottom: "8px",
                                            borderBottom: "2px solid #cbd5e1"
                                        }}>
                                            <span style={{
                                                fontWeight: "700",
                                                fontSize: "0.95rem",
                                                color: "#1e293b"
                                            }}>
                                                Bucket {bucketIndex}
                                            </span>
                                            <span style={{
                                                fontSize: "0.8rem",
                                                color: "#64748b",
                                                fontWeight: "600"
                                            }}>
                                                {bucket.length} στοιχεία
                                            </span>
                                        </div>

                                        <div style={{
                                            display: "flex",
                                            gap: "8px",
                                            flexWrap: "wrap",
                                            minHeight: "35px"
                                        }}>
                                            {bucket.map((item, itemIndex) => (
                                                <motion.div
                                                    key={itemIndex}
                                                    initial={{ opacity: 0, scale: 0.5 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                    style={{
                                                        width: "40px",
                                                        height: "40px",
                                                        borderRadius: "8px",
                                                        background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                                        color: "white",
                                                        fontWeight: "700",
                                                        fontSize: "0.9rem",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
                                                        border: "2px solid rgba(255, 255, 255, 0.5)",
                                                        cursor: stage === 2 && isPlaying ? "pointer" : "default"
                                                    }}
                                                    whileHover={stage === 2 && isPlaying ? { scale: 1.1 } : {}}
                                                    onDoubleClick={() => removeFromBucket(bucketIndex, itemIndex)}
                                                >
                                                    {item}
                                                </motion.div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Control Buttons */}
                    <div style={{
                        textAlign: "center",
                        display: "flex",
                        justifyContent: "center",
                        width: "100%",
                        marginBottom: "20px"
                    }}>
                        <Row>
                            <button
                                onClick={startGame}
                                disabled={isPlaying}
                                style={{
                                    background: isPlaying
                                        ? "linear-gradient(135deg, #64748b 0%, #475569 100%)"
                                        : "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "16px",
                                    padding: "14px 28px",
                                    fontSize: "1rem",
                                    fontWeight: 600,
                                    cursor: isPlaying ? "not-allowed" : "pointer",
                                    boxShadow: isPlaying
                                        ? "0 10px 25px rgba(100, 116, 139, 0.3)"
                                        : "0 10px 25px rgba(59, 130, 246, 0.3), 0 0 0 1px rgba(59, 130, 246, 0.2)",
                                    transition: "all 0.3s ease",
                                    transform: "scale(1)",
                                    marginRight: "12px"
                                }}
                                onMouseEnter={(e) => {
                                    if (!isPlaying) {
                                        e.target.style.transform = "scale(1.02)";
                                        e.target.style.boxShadow = "0 15px 35px rgba(59, 130, 246, 0.4), 0 0 0 2px rgba(59, 130, 246, 0.3)";
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isPlaying) {
                                        e.target.style.transform = "scale(1)";
                                        e.target.style.boxShadow = "0 10px 25px rgba(59, 130, 246, 0.3), 0 0 0 1px rgba(59, 130, 246, 0.2)";
                                    }
                                }}
                            >
                                {isPlaying ? (selLanguage ? "Σε εξέλιξη..." : "In progress...") : (selLanguage ? "Έναρξη" : "Start")}
                            </button>
                        </Row>
                    </div>

                    {/* Status Message */}
                    {statusMessage && (
                        <div style={{
                            textAlign: "center",
                            padding: "20px",
                            borderRadius: "16px",
                            marginTop: "20px",
                            background: statusType === "success"
                                ? "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)"
                                : statusType === "error"
                                    ? "linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)"
                                    : "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                            border: statusType === "success"
                                ? "2px solid #10b981"
                                : statusType === "error"
                                    ? "2px solid #ef4444"
                                    : "2px solid #f59e0b",
                            color: statusType === "success"
                                ? "#059669"
                                : statusType === "error"
                                    ? "#dc2626"
                                    : "#d97706",
                            fontWeight: 600,
                            fontSize: "1rem",
                            boxShadow: statusType === "success"
                                ? "0 10px 25px rgba(16, 185, 129, 0.2)"
                                : statusType === "error"
                                    ? "0 10px 25px rgba(239, 68, 68, 0.2)"
                                    : "0 10px 25px rgba(245, 158, 11, 0.2)"
                        }}>
                            {statusMessage}
                        </div>
                    )}
                </div>
            </Col>
        </div>
    );
};

export default InsertDeleteChaining;